import { useEffect, useMemo, useState } from 'react';
import { decideApproval, listApprovals, type ApprovalRow } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { Icon } from '../../components/Icon';
import { Pending, Stat } from './shared';

/**
 * Reuses the existing workflow engine. This screen raises nothing new and
 * decides nothing locally — it shows what the server routed and posts decisions
 * back through the same audited path.
 */
const MATRIX: Array<{ request: string; requester: string; manager: string; additional: string; final: string }> = [
  { request: 'Leave', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'Administrative manager' },
  { request: 'Work from home', requester: 'Employee', manager: 'Administrative manager', additional: 'HR review', final: 'CEO' },
  { request: 'Attendance correction', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'Administrative manager' },
  { request: 'Expense (under threshold)', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'Administrative manager' },
  { request: 'Expense (over threshold)', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Reimbursement', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Vendor payment', requester: 'Finance', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Payment release', requester: 'Finance', manager: '—', additional: '—', final: 'CEO or authorised signatory' },
  { request: 'Purchase', requester: 'Any', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Promotion', requester: 'Manager', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Salary revision', requester: 'Manager', manager: '—', additional: '—', final: 'CEO' },
  { request: 'Role change', requester: 'Manager', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Resignation', requester: 'Employee', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Travel booking', requester: 'Travel operations', manager: 'Administrative manager', additional: '—', final: 'Administrative manager' },
  { request: 'Vendor engagement', requester: 'Any', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Production deployment', requester: 'Developer', manager: 'Functional manager', additional: '—', final: 'CEO' },
  { request: 'Access permission', requester: 'Any', manager: '—', additional: '—', final: 'CEO' },
  { request: 'Quotation', requester: 'Sales', manager: 'Administrative manager', additional: '—', final: 'CEO' },
  { request: 'Discount', requester: 'Sales', manager: '—', additional: '—', final: 'CEO' },
  { request: 'Client contract', requester: 'Sales', manager: '—', additional: '—', final: 'CEO' },
];

export function ApprovalInbox() {
  const { can } = useAuth();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [tab, setTab] = useState<'inbox' | 'matrix'>('inbox');
  const [mine, setMine] = useState(true);
  const [status, setStatus] = useState('pending');
  const [type, setType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [comment, setComment] = useState<Record<string, string>>({});

  const load = () => listApprovals()
    .then((r) => { setRows(r.items); setError(null); })
    .catch((e: Error) => setError(e.message));

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows
    .filter((r) => !mine || r.actionableByYou)
    .filter((r) => !status || r.status === status)
    .filter((r) => !type || r.requestType === type), [rows, mine, status, type]);

  const types = useMemo(() => [...new Set(rows.map((r) => r.requestType))].sort(), [rows]);

  async function decide(row: ApprovalRow, decision: 'approved' | 'rejected') {
    setBusy(row.id); setError(null); setNotice(null);
    try {
      const res = await decideApproval(row.id, decision, comment[row.id] || undefined);
      setNotice(res.status === 'pending'
        ? `${row.reference}: step ${res.step} of ${res.totalSteps} recorded. It now moves to the next approver.`
        : `${row.reference} ${res.status}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That decision was refused.');
    } finally { setBusy(null); }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="clipboardList" size="xs" /><span>Approvals</span></span>
          <h1>{tab === 'inbox' ? 'Approval Inbox' : 'Approval Matrix'}</h1>
        </div>
        <div className="toolbar">
          <button type="button" className={tab === 'inbox' ? 'btn-primary' : ''} onClick={() => setTab('inbox')}>Inbox</button>
          <button type="button" className={tab === 'matrix' ? 'btn-primary' : ''} onClick={() => setTab('matrix')}>Matrix</button>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {notice && <div className="banner info" style={{ marginBottom: 14 }}>{notice}</div>}

      {tab === 'matrix' ? (
        <div className="panel">
          <div className="panel-head">
            <strong>Configured routing</strong>
            <span className="muted" style={{ fontSize: 12 }}>Thresholds are configuration, not policy</span>
          </div>
          <div className="table-responsive">
            <table>
              <thead>
                <tr><th>Request</th><th>Requester</th><th>Manager</th><th>Additional approval</th><th>Final authority</th></tr>
              </thead>
              <tbody>
                {MATRIX.map((m) => (
                  <tr key={m.request}>
                    <td><strong>{m.request}</strong></td>
                    <td className="muted">{m.requester}</td>
                    <td>{m.manager}</td>
                    <td className="muted">{m.additional}</td>
                    <td>{m.final}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted org-footnote">
            Authority comes from the configured matrix, permissions and live delegations — never from a designation.
            A person holding a finance role does not thereby gain the right to release a payment.
          </p>
        </div>
      ) : (
        <>
          <div className="org-stats">
            <Stat label="Awaiting you" value={rows.filter((r) => r.actionableByYou).length} tone={rows.some((r) => r.actionableByYou) ? 'warn' : 'ok'} />
            <Stat label="Pending overall" value={rows.filter((r) => r.status === 'pending').length} />
            <Stat label="Approved" value={rows.filter((r) => r.status === 'approved').length} />
            <Stat label="Rejected" value={rows.filter((r) => r.status === 'rejected').length} />
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="panel-head">
              <strong>{filtered.length} request{filtered.length === 1 ? '' : 's'}</strong>
              <span className="toolbar">
                <label className="org-inline-check">
                  <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
                  <span>Only mine to action</span>
                </label>
                <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">Any status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select aria-label="Request type" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">Any type</option>
                  {types.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                </select>
              </span>
            </div>

            {filtered.length === 0 ? (
              <div className="empty">
                <strong>Nothing here</strong>
                <p>{mine ? 'No request is currently routed to you.' : 'No request matches these filters.'}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 130 }}>Reference</th>
                      <th>Request</th>
                      <th style={{ width: 130 }}>Raised by</th>
                      <th style={{ width: 130 }}>Awaiting</th>
                      <th style={{ width: 110 }}>Status</th>
                      <th style={{ width: 230 }}>Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{r.reference}</td>
                        <td>
                          <strong>{r.title}</strong>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {r.category} · {r.requestType.replace(/_/g, ' ')}
                            {r.amount != null && ` · ${r.currency} ${r.amount.toLocaleString('en-IN')}`}
                          </div>
                        </td>
                        <td className="muted">{r.requestedBy ?? <Pending />}</td>
                        <td className="muted">{r.awaiting ?? '—'}</td>
                        <td><span className={`org-chip org-chip-${r.status}`}>{r.status}</span></td>
                        <td>
                          {r.actionableByYou && can('approval.act') ? (
                            <div className="org-decision">
                              <input placeholder="Comment (optional)" value={comment[r.id] ?? ''} onChange={(e) => setComment({ ...comment, [r.id]: e.target.value })} />
                              <span>
                                <button type="button" className="btn-primary" disabled={busy === r.id} onClick={() => decide(r, 'approved')}>Approve</button>
                                <button type="button" disabled={busy === r.id} onClick={() => decide(r, 'rejected')}>Reject</button>
                              </span>
                            </div>
                          ) : (
                            <span className="muted">{r.status === 'pending' ? 'Not yours to decide' : '—'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted org-footnote">
              You cannot approve a request you raised or that concerns you. Blocked attempts are written to the audit log.
            </p>
          </div>
        </>
      )}
    </>
  );
}
